/**
 * DrillLibraryPickerSheet — full-screen bottom Sheet that embeds the DrillLibrary
 * in pick-one mode for the mobile drill plan wizard.
 */

import React from 'react';
import { ArrowLeft } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import DrillLibrary from '@/components/drill-library/DrillLibrary';
import { useAuth } from '@/contexts/AuthContext';

interface DrillLibraryPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  slotLabel: string;
  onSelect: (drillId: string) => void;
}

const DrillLibraryPickerSheet: React.FC<DrillLibraryPickerSheetProps> = ({
  isOpen,
  onClose,
  slotLabel,
  onSelect,
}) => {
  const { user } = useAuth();

  const handlePick = (drillId: string) => {
    onSelect(drillId);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        className="h-[92dvh] max-h-[92dvh] flex flex-col p-0 gap-0 rounded-t-2xl [&>button.absolute]:hidden"
      >
        <SheetHeader className="px-3 py-2.5 border-b flex-shrink-0 space-y-0">
          <div className="flex items-center gap-2 pr-10">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 shrink-0"
              onClick={onClose}
              aria-label="Back to drill plan"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1 text-left">
              <SheetTitle className="text-sm leading-tight">
                Select drill — <span className="font-bold">{slotLabel}</span>
              </SheetTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Tap a drill card to use it in this slot.
              </p>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 pb-4">
          <DrillLibrary user={user} onPickDrill={handlePick} />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DrillLibraryPickerSheet;
